import { type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, BookOpen, Landmark, Banknote, FileSignature, Settings as SettingsIcon, CreditCard, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import { useCompany } from './CompanyProvider';

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter', plus: 'Plus', professional: 'Professional', business: 'Business',
};

/**
 * "Mehr"-Seite der Mobile-Tab-Bar: alle Module, die nicht als eigener Tab
 * sichtbar sind, plus Konto/Abmelden. Am Desktop steht alles in der Sidebar —
 * diese Seite ist dort nur über den direkten Link erreichbar.
 */
export default function More(): ReactElement {
  const { user, logout } = useAuth();
  const { active } = useCompany();
  const navigate = useNavigate();
  const plan = active?.plan ?? 'starter';
  const isStarter = plan === 'starter';

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  const items = [
    { to: '/app/kunden', label: 'Kunden', icon: Users },
    { to: '/app/buchhaltung', label: 'Buchhaltung', icon: BookOpen },
    { to: '/app/steuern', label: 'Steuern', icon: Landmark },
    { to: '/app/banking', label: 'Banking & Mahnwesen', icon: Banknote },
    { to: '/app/formulare', label: 'Formulare', icon: FileSignature },
    { to: '/app/einstellungen', label: 'Einstellungen', icon: SettingsIcon },
    { to: '/app/einstellungen/tarif', label: 'Tarif', icon: CreditCard },
  ];

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-1 text-2xl font-bold text-gray-900">Mehr</h1>
      <p className="mb-6 text-sm text-gray-500">Alle Funktionen & Einstellungen</p>

      {/* Konto-Karte */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{user?.name ?? 'Angemeldet'}</span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isStarter ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
            <CreditCard className="h-3 w-3" />
            {PLAN_LABEL[plan] ?? plan}
          </span>
        </div>
        <div className="truncate text-xs text-gray-500">{user?.email}</div>
        <Link to="/app/einstellungen/tarif" className={`mt-1 inline-block text-xs font-medium hover:underline ${isStarter ? 'text-amber-700' : 'text-blue-600'}`}>
          {isStarter ? 'Upgrade →' : 'Tarif verwalten →'}
        </Link>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {items.map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5 text-sm font-medium text-gray-800 transition last:border-b-0 hover:bg-gray-50">
            <Icon className="h-5 w-5 text-gray-400" />
            <span className="flex-1">{label}</span>
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </Link>
        ))}
      </div>

      <button onClick={handleLogout} className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50">
        <LogOut className="h-4 w-4" />
        Abmelden
      </button>
    </div>
  );
}
